import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, Type, Image, Link as LinkIcon, Mail, Phone, Clock, MapPin, Plus, Trash2, Save, RefreshCw, Eye, EyeOff, X, Share2, List
} from "lucide-react";



const SERVER_URL = "https://allinoneai-d1a5a1b54c64.herokuapp.com";

const AdminNavbar = () => {
  const [data, setData] = useState({
    logo: { dark: "", light: "" },
    navLinks: [],
    ctaButton: { text: "", url: "", visible: true },
    contactInfo: { address: "", email: "", phone: "", workingHours: "" },
    socialLinks: [],
    offcanvasDescription: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${SERVER_URL}/api/navbar-v5`);
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (err) {
        showMessage("Failed to fetch data", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  // Handle Nested Object Change
  const handleNestedChange = (category, field, value) => {
    setData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // --- List Handlers ---
  
  // Nav Links
  const addNavItem = () => {
    const newItem = { id: Date.now().toString(), label: "New Link", url: "#", order: data.navLinks.length + 1 };
    setData(prev => ({ ...prev, navLinks: [...prev.navLinks, newItem] }));
  };
  const updateNavItem = (index, field, value) => {
    const newItems = [...data.navLinks];
    newItems[index] = { ...newItems[index], [field]: value };
    setData({ ...data, navLinks: newItems });
  };
  const removeNavItem = (index) => {
    const newItems = data.navLinks.filter((_, i) => i !== index);
    setData({ ...data, navLinks: newItems });
  };

  // Social Links
  const addSocialLink = () => {
    const newLink = { id: Date.now().toString(), platform: "", url: "#", icon: "fab fa-" };
    setData(prev => ({ ...prev, socialLinks: [...prev.socialLinks, newLink] }));
  };
  const updateSocialLink = (index, field, value) => {
    const newLinks = [...data.socialLinks];
    newLinks[index] = { ...newLinks[index], [field]: value };
    setData({ ...data, socialLinks: newLinks });
  };
  const removeSocialLink = (index) => {
    const newLinks = data.socialLinks.filter((_, i) => i !== index);
    setData({ ...data, socialLinks: newLinks });
  };

  // Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.put(`${SERVER_URL}/api/navbar-v5`, data);
      showMessage("Navbar updated successfully!", "success");
    } catch (err) {
      showMessage("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset to default settings?")) return;
    try {
      const res = await axios.post(`${SERVER_URL}/api/navbar-v5/reset`);
      if (res.data.success) {
        setData(res.data.data);
        showMessage("Reset successful", "success");
      }
    } catch (err) {
      showMessage("Failed to reset", "error");
    }
  };

  const toggleStatus = async () => {
    try {
      await axios.patch(`${SERVER_URL}/api/navbar-v5/toggle`);
      const newStatus = !data.isActive;
      setData({ ...data, isActive: newStatus });
      showMessage(`Navbar ${newStatus ? "activated" : "deactivated"}`, "success");
    } catch (err) {
      showMessage("Failed to toggle status", "error");
    }
  };

  if (loading) return <div className="p-5 text-center">Loading Admin Panel...</div>;

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <h3 className="fw-bold">Navbar Management</h3>
        <div className="d-flex gap-2">
            <button 
                className={`btn btn-sm ${data.isActive ? 'btn-success' : 'btn-outline-secondary'} d-flex align-items-center gap-2`}
                onClick={toggleStatus}
            >
                {data.isActive ? <Eye size={18} /> : <EyeOff size={18} />}
                {data.isActive ? "Active" : "Disabled"}
            </button>
          <button className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" onClick={handleReset} disabled={saving}>
            <RefreshCw size={18} /> Reset
          </button>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-2" onClick={handleSubmit} disabled={saving}>
            <Save size={18} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : message.type} mb-4`}>
          {message.text}
          <button type="button" className="btn-close float-end" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row">
          
          {/* Logo & Description */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Image size={20} /> Branding
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Logo Dark (URL)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.logo?.dark || ""}
                              onChange={(e) => handleNestedChange('logo', 'dark', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Logo Light (URL)</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.logo?.light || ""}
                              onChange={(e) => handleNestedChange('logo', 'light', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="mb-0">
                        <label className="form-label small fw-bold">Offcanvas Description</label>
                        <textarea
                          className="form-control"
                          rows="2"
                          value={data.offcanvasDescription}
                          onChange={(e) => setData({ ...data, offcanvasDescription: e.target.value })}
                        />
                    </div>
                </div>
            </div>
          </div>

          {/* Nav Links */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <List size={20} /> Navigation Links
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addNavItem}>
                        <Plus size={16} /> Add Link
                    </button>
                </div>
                <div className="card-body">
                    {data.navLinks?.map((link, index) => (
                        <div key={link.id || index} className="row g-2 align-items-start mb-3 pb-3 border-bottom">
                            <div className="col-12 mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="badge bg-secondary">Link #{index + 1}</span>
                                    <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeNavItem(index)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">Label</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Label"
                                  value={link.label}
                                  onChange={(e) => updateNavItem(index, 'label', e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">URL</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="URL"
                                  value={link.url}
                                  onChange={(e) => updateNavItem(index, 'url', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                    {(!data.navLinks || data.navLinks.length === 0) && <p className="text-center text-muted py-3">No navigation links added.</p>}
                </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <LinkIcon size={20} /> CTA Button
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Button Text</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.ctaButton?.text}
                              onChange={(e) => handleNestedChange('ctaButton', 'text', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold">Button URL</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.ctaButton?.url}
                              onChange={(e) => handleNestedChange('ctaButton', 'url', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm h-100">
                <div className="card-header bg-white fw-bold py-3 d-flex align-items-center gap-2">
                    <Mail size={20} /> Contact Info
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold"><MapPin size={14} className="me-1"/> Address</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.contactInfo?.address}
                              onChange={(e) => handleNestedChange('contactInfo', 'address', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold"><Mail size={14} className="me-1"/> Email</label>
                            <input
                              type="email"
                              className="form-control"
                              value={data.contactInfo?.email}
                              onChange={(e) => handleNestedChange('contactInfo', 'email', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold"><Phone size={14} className="me-1"/> Phone</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.contactInfo?.phone}
                              onChange={(e) => handleNestedChange('contactInfo', 'phone', e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="form-label small fw-bold"><Clock size={14} className="me-1"/> Working Hours</label>
                            <input
                              type="text"
                              className="form-control"
                              value={data.contactInfo?.workingHours}
                              onChange={(e) => handleNestedChange('contactInfo', 'workingHours', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* Social Links */}
          <div className="col-lg-12 mb-4">
            <div className="card shadow-sm">
                <div className="card-header bg-white fw-bold py-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <Share2 size={20} /> Social Links
                    </div>
                    <button type="button" className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={addSocialLink}>
                        <Plus size={16} /> Add Link
                    </button>
                </div>
                <div className="card-body">
                    {data.socialLinks?.map((link, index) => (
                        <div key={link.id || index} className="row g-2 align-items-start mb-3 pb-3 border-bottom">
                            <div className="col-12 mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span className="badge bg-secondary">Link #{index + 1}</span>
                                    <button type="button" className="btn btn-sm btn-link text-danger p-0" onClick={() => removeSocialLink(index)}>
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">Icon Class (e.g. fab fa-facebook)</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Icon Class"
                                  value={link.icon}
                                  onChange={(e) => updateSocialLink(index, 'icon', e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <label className="small text-muted">URL</label>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="URL"
                                  value={link.url}
                                  onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                    {(!data.socialLinks || data.socialLinks.length === 0) && <p className="text-center text-muted py-3">No social links added.</p>}
                </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
};

export default AdminNavbar;